import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  AlertTriangle, CheckCircle, Clock, History, Loader2,
  Mail, Send, Settings, ToggleLeft, ToggleRight
} from 'lucide-react';

export default function RappelsTab({
  expiringAdhesions,
  reminderConfig,
  reminderHistory,
  triggeringReminders,
  sendingReminderId,
  updatingConfig,
  onTriggerReminders,
  onSendIndividualReminder,
  onToggleReminders,
  onUpdateIntervals,
}) {
  return (
    <div className="space-y-6">
      {/* Configuration des rappels */}
      <Card className="border-2 border-blue-200" data-testid="reminder-config-card">
        <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-t-lg">
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Configuration des rappels automatiques
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleReminders}
              disabled={updatingConfig}
              className="text-white hover:bg-white/20"
              data-testid="toggle-reminders-btn"
            >
              {updatingConfig ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : reminderConfig?.enabled ? (
                <ToggleRight className="h-6 w-6 text-green-300" />
              ) : (
                <ToggleLeft className="h-6 w-6 text-red-300" />
              )}
              <span className="ml-2 text-sm">
                {reminderConfig?.enabled ? 'Active' : 'Desactive'}
              </span>
            </Button>
          </CardTitle>
          <CardDescription className="text-blue-100">
            Les rappels sont envoyes automatiquement chaque jour a 9h00 aux adhesions qui expirent bientot
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-5">
          <div className="grid md:grid-cols-3 gap-4">
            {/* Intervalles */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-gray-800 mb-3 text-sm">Intervalles de rappel (jours avant expiration)</h4>
              <div className="flex flex-wrap gap-2">
                {[30, 15, 7, 3, 1].map(day => {
                  const isActive = (reminderConfig?.intervals_days || [30, 15, 7, 1]).includes(day);
                  return (
                    <Button
                      key={day}
                      size="sm"
                      variant={isActive ? 'default' : 'outline'}
                      className={isActive ? 'bg-blue-600 hover:bg-blue-700' : ''}
                      onClick={() => {
                        const current = reminderConfig?.intervals_days || [30, 15, 7, 1];
                        const updated = isActive
                          ? current.filter(d => d !== day)
                          : [...current, day].sort((a, b) => b - a);
                        onUpdateIntervals(updated);
                      }}
                      disabled={updatingConfig}
                    >
                      J-{day}
                    </Button>
                  );
                })}
              </div>
            </div>

            {/* Dernier envoi */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-gray-800 mb-3 text-sm">Dernier envoi automatique</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-600">
                    {reminderConfig?.last_run
                      ? new Date(reminderConfig.last_run).toLocaleString('fr-FR')
                      : 'Jamais execute'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-600">
                    {reminderConfig?.emails_sent_today || 0} email(s) envoye(s)
                  </span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="p-4 bg-gray-50 rounded-lg flex flex-col justify-center gap-3">
              <Button
                onClick={onTriggerReminders}
                disabled={triggeringReminders || !reminderConfig?.enabled}
                className="bg-blue-600 hover:bg-blue-700 w-full"
                data-testid="trigger-all-reminders-btn"
              >
                {triggeringReminders ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Envoyer tous les rappels maintenant
              </Button>
              <p className="text-xs text-gray-500 text-center">
                Envoie un rappel a toutes les adhesions proches de l'expiration
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Adhesions expirantes */}
      <Card className="border-2 border-orange-200" data-testid="expiring-adhesions-card">
        <CardHeader className="bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-t-lg">
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Adhesions expirant dans les 30 prochains jours
            <Badge className="bg-white text-slate-800/20 text-white ml-auto font-semibold">{expiringAdhesions.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-5">
          {expiringAdhesions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-400" />
              <p className="font-medium">Aucune adhesion ne expire dans les 30 prochains jours</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {expiringAdhesions.map((adhesion) => {
                const email = adhesion.email || '';
                const daysLeft = adhesion.days_remaining;
                const isSending = sendingReminderId === adhesion.id;

                let urgencyColor = 'bg-green-100 text-green-800 border-green-200';
                if (daysLeft <= 1) urgencyColor = 'bg-red-600 text-white border-red-700';
                else if (daysLeft <= 7) urgencyColor = 'bg-orange-600 text-white border-orange-700';
                else if (daysLeft <= 15) urgencyColor = 'bg-amber-600 text-white border-amber-700';
                else urgencyColor = 'bg-blue-600 text-white border-blue-700';

                return (
                  <div key={adhesion.id} className="flex items-center gap-3 p-3 bg-gray-50 hover:bg-gray-100 rounded-lg border transition-colors">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{adhesion.raison_sociale || 'N/A'}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-500">{email || "Pas d'email"}</span>
                        {adhesion.territoire && (
                          <Badge variant="outline" className="text-xs">{adhesion.territoire}</Badge>
                        )}
                      </div>
                    </div>
                    <Badge className={`${urgencyColor} text-xs font-bold px-3`} data-testid={`days-badge-${adhesion.id}`}>
                      {daysLeft != null ? (daysLeft <= 0 ? 'Expire' : `J-${daysLeft}`) : 'N/A'}
                    </Badge>
                    <Button
                      size="sm"
                      onClick={() => onSendIndividualReminder(adhesion.id, email)}
                      disabled={isSending || !email}
                      className="bg-blue-600 hover:bg-blue-700 shrink-0"
                      data-testid={`send-reminder-${adhesion.id}`}
                      title={email ? `Envoyer un rappel a ${email}` : "Pas d'email configure"}
                    >
                      {isSending ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Send className="h-3 w-3" />
                      )}
                      <span className="ml-1 text-xs hidden sm:inline">Rappeler</span>
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Historique des rappels */}
      <Card data-testid="reminder-history-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <History className="h-5 w-5 text-gray-600" />
            Historique des rappels envoyes
            <Badge variant="outline" className="ml-auto">{reminderHistory.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {reminderHistory.length === 0 ? (
            <div className="text-center py-6 text-gray-500 text-sm">
              Aucun rappel envoye pour le moment
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Entreprise</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Email</th>
                    <th className="text-center px-3 py-2 font-medium text-gray-600">J-30</th>
                    <th className="text-center px-3 py-2 font-medium text-gray-600">J-15</th>
                    <th className="text-center px-3 py-2 font-medium text-gray-600">J-7</th>
                    <th className="text-center px-3 py-2 font-medium text-gray-600">J-1</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Dernier rappel manuel</th>
                  </tr>
                </thead>
                <tbody>
                  {reminderHistory.map((a) => (
                    <tr key={a.id} className="border-b hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium">{a.raison_sociale || 'N/A'}</td>
                      <td className="px-3 py-2 text-gray-500 text-xs">{a.email || a.user_email || '-'}</td>
                      <td className="px-3 py-2 text-center">
                        {a.reminder_sent_30d ? <CheckCircle className="h-4 w-4 text-green-500 mx-auto" /> : <span className="text-gray-300">-</span>}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {a.reminder_sent_15d ? <CheckCircle className="h-4 w-4 text-green-500 mx-auto" /> : <span className="text-gray-300">-</span>}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {a.reminder_sent_7d ? <CheckCircle className="h-4 w-4 text-green-500 mx-auto" /> : <span className="text-gray-300">-</span>}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {a.reminder_sent_1d ? <CheckCircle className="h-4 w-4 text-green-500 mx-auto" /> : <span className="text-gray-300">-</span>}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500">
                        {a.last_manual_reminder_at
                          ? new Date(a.last_manual_reminder_at).toLocaleString('fr-FR')
                          : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
